const CACHE_PREFIX = "nexrojgar-public-cache:";

const DEFAULT_MAX_AGE = 24 * 60 * 60 * 1000;

/*
 * Hot in-memory layer for same-tab navigation. localStorage remains the
 * persistence layer across reloads, but repeat route visits no longer need
 * JSON.parse + storage access before painting cached public data.
 */
const memoryCache = new Map();

const isStorageAvailable = () => {
  return (
    typeof window !== "undefined" &&
    typeof window.localStorage !== "undefined"
  );
};

const createCacheKey = (key) => {
  return `${CACHE_PREFIX}${String(key || "").trim()}`;
};

const isValidCacheKey = (key) => {
  return Boolean(String(key || "").trim());
};

const isCacheableValue = (value) => value !== undefined;

const normalizeEntry = (entry) => {
  if (
    !entry ||
    typeof entry !== "object" ||
    !Object.prototype.hasOwnProperty.call(entry, "data") ||
    typeof entry.savedAt !== "number"
  ) {
    return null;
  }

  return {
    data: entry.data,
    savedAt: entry.savedAt,
    stale: Boolean(entry.stale),
    version: entry.version || 2,
  };
};

export const savePublicCache = (key, data) => {
  if (!isValidCacheKey(key) || !isCacheableValue(data)) {
    return false;
  }

  const normalizedKey = String(key).trim();
  const cacheEntry = {
    data,
    savedAt: Date.now(),
    stale: false,
    version: 2,
  };

  memoryCache.set(normalizedKey, cacheEntry);

  if (!isStorageAvailable()) {
    return true;
  }

  try {
    window.localStorage.setItem(
      createCacheKey(normalizedKey),
      JSON.stringify(cacheEntry),
    );

    return true;
  } catch (error) {
    /* Cache is optional. Memory cache still keeps same-tab navigation fast. */
    if (process.env.NODE_ENV !== "production") {
      console.warn("Unable to save public cache:", error);
    }

    return true;
  }
};

export const getPublicCache = (
  key,
  {
    maxAge = DEFAULT_MAX_AGE,
    allowExpired = false,
  } = {},
) => {
  if (!isValidCacheKey(key)) {
    return null;
  }

  const normalizedKey = String(key).trim();
  const now = Date.now();

  const toResult = (entry) => {
    const normalizedEntry = normalizeEntry(entry);
    if (!normalizedEntry) return null;

    const age = now - normalizedEntry.savedAt;
    const expired =
      Number.isFinite(maxAge) && maxAge >= 0 && age > maxAge;

    if (expired && !allowExpired) {
      return null;
    }

    return {
      data: normalizedEntry.data,
      savedAt: normalizedEntry.savedAt,
      age,
      expired,
      stale: normalizedEntry.stale,
      version: normalizedEntry.version,
    };
  };

  const memoryResult = toResult(memoryCache.get(normalizedKey));

  if (memoryResult) {
    return memoryResult;
  }

  if (!isStorageAvailable()) {
    return null;
  }

  try {
    const storedValue = window.localStorage.getItem(
      createCacheKey(normalizedKey),
    );

    if (!storedValue) {
      return null;
    }

    const parsedValue = JSON.parse(storedValue);
    const normalizedEntry = normalizeEntry(parsedValue);

    if (!normalizedEntry) {
      removePublicCache(normalizedKey);
      return null;
    }

    const result = toResult(normalizedEntry);

    if (!result) {
      removePublicCache(normalizedKey);
      return null;
    }

    memoryCache.set(normalizedKey, normalizedEntry);
    return result;
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Unable to read public cache:", error);
    }

    removePublicCache(normalizedKey);
    return null;
  }
};

export const removePublicCache = (key) => {
  if (!isValidCacheKey(key)) {
    return false;
  }

  const normalizedKey = String(key).trim();
  memoryCache.delete(normalizedKey);

  if (!isStorageAvailable()) {
    return true;
  }

  try {
    window.localStorage.removeItem(createCacheKey(normalizedKey));
    return true;
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Unable to remove public cache:", error);
    }

    return false;
  }
};

export const clearAllPublicCache = () => {
  memoryCache.clear();

  if (!isStorageAvailable()) {
    return 0;
  }

  let removedCount = 0;

  try {
    const keysToRemove = [];

    for (let index = 0; index < window.localStorage.length; index += 1) {
      const storageKey = window.localStorage.key(index);

      if (storageKey?.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(storageKey);
      }
    }

    keysToRemove.forEach((storageKey) => {
      window.localStorage.removeItem(storageKey);
      removedCount += 1;
    });

    return removedCount;
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Unable to clear public cache:", error);
    }

    return removedCount;
  }
};

/*
 * Admin mutations in the same browser mark public reads stale instead of
 * deleting them. Public pages can still paint instantly, then silently fetch
 * the just-updated server data in the background.
 */
export const markPublicDataCacheStale = () => {
  for (const [key, entry] of memoryCache.entries()) {
    memoryCache.set(key, {
      ...entry,
      stale: true,
    });
  }

  if (!isStorageAvailable()) {
    return;
  }

  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const storageKey = window.localStorage.key(index);

      if (!storageKey?.startsWith(CACHE_PREFIX)) {
        continue;
      }

      const rawValue = window.localStorage.getItem(storageKey);
      if (!rawValue) continue;

      const parsedValue = normalizeEntry(JSON.parse(rawValue));
      if (!parsedValue) continue;

      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          ...parsedValue,
          stale: true,
        }),
      );
    }
  } catch {
    /* Best-effort invalidation only. */
  }
};

export const getPublicCacheInfo = (key) => {
  const cache = getPublicCache(key, {
    maxAge: Number.POSITIVE_INFINITY,
    allowExpired: true,
  });

  if (!cache) {
    return null;
  }

  return {
    savedAt: cache.savedAt,
    age: cache.age,
    expired: cache.expired,
    stale: cache.stale,
    dataType: Array.isArray(cache.data)
      ? "array"
      : cache.data === null
        ? "null"
        : typeof cache.data,
  };
};
